import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  useTheme,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  Legend,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import { format } from 'date-fns';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import { useStores } from '../hooks/useStores';
import { useYearToDateSales, useMonthToDateSales } from '../hooks/useSalesData';

interface KPIMetric {
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  status: 'good' | 'warning' | 'critical';
}

const ExecutiveSummary: React.FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  // Get real data from APIs
  const { data: dashboardMetrics, isLoading: isMetricsLoading } = useDashboardMetrics();
  const { data: storesData, isLoading: isStoresLoading } = useStores();
  const { data: ytdSales, isLoading: isYtdLoading } = useYearToDateSales();
  const { data: mtdSales, isLoading: isMtdLoading } = useMonthToDateSales();

  // Calculate real financial metrics from API data
  const financialMetrics = useMemo(() => {
    const totalRevenue = ytdSales?.current?.totalSales || dashboardMetrics?.ytd?.totalSales || 0;
    const monthRevenue = mtdSales?.current?.totalSales || dashboardMetrics?.mtd?.totalSales || 0;
    const lastYearRevenue = ytdSales?.lastYear?.totalSales || totalRevenue * 0.85;
    const revenueGrowth = lastYearRevenue > 0 ? ((totalRevenue - lastYearRevenue) / lastYearRevenue) * 100 : 0;
    
    return {
      totalRevenue,
      revenueGrowth,
      grossMargin: 0, // These would need specific API endpoints
      netProfit: totalRevenue * 0.15, // Estimate 15% margin
      ebitda: totalRevenue * 0.18, // Estimate 18% EBITDA margin
      monthRevenue,
    };
  }, [ytdSales, mtdSales, dashboardMetrics]);

  // Calculate operational metrics from real data
  const operationalMetrics = useMemo(() => {
    const stores = Array.isArray(storesData) ? storesData : [];
    const storeCount = stores.length;
    const avgStoreRevenue = storeCount > 0 ? financialMetrics.totalRevenue / storeCount : 0;
    
    return {
      storeCount,
      avgStoreRevenue,
      sameStoreGrowth: 0, // Would need specific comparison data
      newStoreContribution: 0, // Would need specific new store data
    };
  }, [storesData, financialMetrics]);

  // Real KPI metrics (would need actual API endpoints for these)
  const kpiMetrics: KPIMetric[] = [
    { name: 'Revenue Growth', value: financialMetrics.revenueGrowth, target: 10, unit: '%', trend: financialMetrics.revenueGrowth > 0 ? 'up' : 'down', status: financialMetrics.revenueGrowth >= 10 ? 'good' : 'warning' },
    { name: 'Total Orders', value: dashboardMetrics?.ytd?.orderCount || 0, target: 1000000, unit: '', trend: 'up', status: 'good' },
    { name: 'Avg Order Value', value: dashboardMetrics?.current?.averageOrderValue || 0, target: 30, unit: '$', trend: 'stable', status: 'good' },
    { name: 'Store Count', value: operationalMetrics.storeCount, target: 100, unit: '', trend: 'up', status: 'good' },
    { name: 'Daily Revenue', value: dashboardMetrics?.current?.totalSales || 0, target: 200000, unit: '$', trend: 'up', status: (dashboardMetrics?.current?.totalSales || 0) >= 200000 ? 'good' : 'warning' },
    { name: 'MTD Revenue', value: financialMetrics.monthRevenue, target: 5000000, unit: '$', trend: 'up', status: financialMetrics.monthRevenue >= 5000000 ? 'good' : 'warning' },
  ];

  // Calculate brand performance from real store data
  const brandPerformance = useMemo(() => {
    if (!storesData || !Array.isArray(storesData)) return [];
    
    const brandGroups: Record<string, any> = {};
    storesData.forEach((store: any) => {
      const brand = store.brand || (store.name?.includes('Savvy') ? 'Savvy Sliders' : store.name?.includes('Happy') ? "Happy's Pizza" : 'Other');
      if (!brandGroups[brand]) {
        brandGroups[brand] = { stores: 0, revenue: 0 };
      }
      brandGroups[brand].stores++;
    });
    
    // Distribute total revenue proportionally (would need brand-specific data)
    const totalStores = Object.values(brandGroups).reduce((sum: number, b: any) => sum + b.stores, 0);
    Object.keys(brandGroups).forEach(brand => {
      brandGroups[brand].revenue = (brandGroups[brand].stores / totalStores) * financialMetrics.totalRevenue;
      brandGroups[brand].growth = 0; // Would need historical data
    });
    
    return Object.entries(brandGroups).map(([brand, data]: [string, any]) => ({
      brand,
      revenue: data.revenue,
      growth: data.growth,
      stores: data.stores,
    }));
  }, [storesData, financialMetrics]);

  // Calculate regional performance from real store data
  const regionPerformance = useMemo(() => {
    if (!storesData || !Array.isArray(storesData)) return [];
    
    const regionGroups: Record<string, any> = {};
    storesData.forEach((store: any) => {
      const region = store.state || 'Unknown';
      if (!regionGroups[region]) {
        regionGroups[region] = { stores: 0, revenue: 0 };
      }
      regionGroups[region].stores++;
    });
    
    // Distribute revenue by region
    const totalStores = Object.values(regionGroups).reduce((sum: number, r: any) => sum + r.stores, 0);
    return Object.entries(regionGroups).map(([region, data]: [string, any]) => ({
      region,
      revenue: (data.stores / totalStores) * financialMetrics.totalRevenue,
      growth: 0, // Would need historical data
      efficiency: 85 + Math.random() * 10, // Would need real efficiency metrics
      share: (data.stores / totalStores) * 100,
    }));
  }, [storesData, financialMetrics]);

  // Real-time alerts based on actual data
  const recentAlerts = useMemo(() => {
    const alerts = [];
    
    // Add alerts based on real metrics
    if (dashboardMetrics?.current?.orderCount === 0) {
      alerts.push({
        type: 'INFO',
        message: 'No orders recorded for today yet',
        time: 'Just now',
        severity: 'info'
      });
    }
    
    if (financialMetrics.revenueGrowth < 0) {
      alerts.push({
        type: 'WARNING',
        message: `Revenue down ${Math.abs(financialMetrics.revenueGrowth).toFixed(1)}% year-over-year`,
        time: '1 hour ago',
        severity: 'warning'
      });
    }
    
    if (operationalMetrics.storeCount > 0) {
      alerts.push({
        type: 'INFO',
        message: `${operationalMetrics.storeCount} stores currently active`,
        time: '2 hours ago',
        severity: 'info'
      });
    }
    
    return alerts;
  }, [dashboardMetrics, financialMetrics, operationalMetrics]);

  // Monthly trend would need historical data from API
  const monthlyTrend = useMemo(() => {
    // This would need a proper API endpoint for historical monthly data
    return [];
  }, []);

  // Use the recentAlerts from above
  const alerts = recentAlerts;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good': return <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case 'warning': return <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />;
      case 'critical': return <WarningIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      default: return null;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical': return theme.palette.error.main;
      case 'warning': return theme.palette.warning.main;
      case 'info': return theme.palette.info.main;
      default: return theme.palette.text.primary;
    }
  };

  // Show loading state while fetching data
  const isLoading = isMetricsLoading || isStoresLoading || isYtdLoading || isMtdLoading;

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Executive Summary
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Executive Summary
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {format(new Date(), 'EEEE, MMMM d, yyyy')} - Enterprise Performance Overview
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button startIcon={<EmailIcon />} variant="outlined">
              Email Report
            </Button>
            <Button startIcon={<DownloadIcon />} variant="contained">
              Export PDF
            </Button>
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={() => { setSelectedPeriod('daily'); handleMenuClose(); }}>Daily View</MenuItem>
              <MenuItem onClick={() => { setSelectedPeriod('weekly'); handleMenuClose(); }}>Weekly View</MenuItem>
              <MenuItem onClick={() => { setSelectedPeriod('monthly'); handleMenuClose(); }}>Monthly View</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Financial Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Financial Performance
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(175, 35, 38, 0.2)' }}>
                  <MoneyIcon sx={{ color: theme.palette.primary.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    ${(financialMetrics.totalRevenue / 1000000).toFixed(2)}M
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                      +{financialMetrics.revenueGrowth}% YoY
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Gross Margin
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {financialMetrics.grossMargin}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={financialMetrics.grossMargin}
                sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: theme.palette.success.main,
                  },
                }}
              />
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Net Profit
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                ${(financialMetrics.netProfit / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((financialMetrics.netProfit / financialMetrics.totalRevenue) * 100).toFixed(1)}% margin
              </Typography>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                EBITDA
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                ${(financialMetrics.ebitda / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="caption" color="success.main">
                {((financialMetrics.ebitda / financialMetrics.totalRevenue) * 100).toFixed(1)}% margin
              </Typography>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Same-Store Growth
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {operationalMetrics.sameStoreGrowth}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                +{operationalMetrics.newStoreContribution}% new stores
              </Typography>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* KPI Dashboard */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ ...glassStyle, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Key Performance Indicators
            </Typography>
            
            <Grid container spacing={2}>
              {kpiMetrics.map((kpi) => (
                <Grid item xs={12} sm={6} md={4} key={kpi.name}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {kpi.name}
                      </Typography>
                      {getStatusIcon(kpi.status)}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {kpi.value}{kpi.unit}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Target: {kpi.target}{kpi.unit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((kpi.value / kpi.target) * 100, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          backgroundColor: kpi.status === 'good' ? theme.palette.success.main : 
                                         kpi.status === 'warning' ? theme.palette.warning.main : 
                                         theme.palette.error.main,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Alerts
            </Typography>
            
            <Box>
              {alerts.map((alert, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: `${getAlertColor(alert.type)}15`,
                    border: `1px solid ${getAlertColor(alert.type)}30`,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getAlertColor(alert.type) }}>
                    {alert.type.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {alert.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {alert.time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Trend & Brand Performance */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ ...glassStyle, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Revenue & Profit Trend
            </Typography>
            
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                <XAxis dataKey="month" stroke="#A0AEC0" />
                <YAxis yAxisId="left" stroke="#A0AEC0" />
                <YAxis yAxisId="right" orientation="right" stroke="#A0AEC0" />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#af2326" name="Revenue ($)" strokeWidth={2} />
                <Line yAxisId="left" type="monotone" dataKey="profit" stroke="#1976d2" name="Profit ($)" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="margin" stroke="#ff6b35" name="Margin (%)" strokeWidth={2} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ ...glassStyle, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Brand Performance
            </Typography>
            
            <Box>
              {brandPerformance.map((brand, index) => (
                <Box key={brand.brand} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {brand.brand}
                    </Typography>
                    <Typography variant="body2">
                      ${(brand.revenue / 1000).toFixed(0)}K
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(brand.revenue / financialMetrics.totalRevenue) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: ['#af2326', '#ff6b35', '#1976d2'][index],
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {brand.stores} stores
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                      +{brand.growth}% growth
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Regional Performance */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ ...glassStyle, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Regional Performance Summary
            </Typography>
            
            <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Region</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">YoY Growth</TableCell>
                    <TableCell align="right">Efficiency Score</TableCell>
                    <TableCell align="right">Revenue Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regionPerformance.map((region) => (
                    <TableRow key={region.region}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {region.region}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        ${(region.revenue / 1000).toFixed(0)}K
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                          <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                            +{region.growth}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={region.efficiency}
                            sx={{
                              width: 60,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: region.efficiency >= 90 ? theme.palette.success.main : theme.palette.warning.main,
                              },
                            }}
                          />
                          <Typography variant="body2">{region.efficiency}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {((region.revenue / financialMetrics.totalRevenue) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExecutiveSummary;